import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, TrendingUp } from 'lucide-react';

interface SkillBarAnimationProps {
    skillArea: string;
    onComplete?: () => void;
}

export const SkillBarAnimation: React.FC<SkillBarAnimationProps> = ({ skillArea, onComplete }) => {
    const [progress, setProgress] = useState(0);
    const [showConfetti, setShowConfetti] = useState(false);

    useEffect(() => {
        // Stage 1: Initial pause
        const startTimeout = setTimeout(() => {
            setProgress(100);
            setShowConfetti(true);
        }, 500);

        // Stage 2: Signal completion to parent
        const completeTimeout = setTimeout(() => {
            if (onComplete) onComplete();
        }, 3500);

        return () => {
            clearTimeout(startTimeout);
            clearTimeout(completeTimeout);
        };
    }, [onComplete]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 border border-slate-100 overflow-hidden relative"
            >
                {/* Success Icon Bloom */}
                <div className="flex justify-center mb-6 relative">
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
                        className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 relative z-10"
                    >
                        <CheckCircle2 className="w-10 h-10" />
                    </motion.div>
                    {showConfetti && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.5 }}
                            animate={{ opacity: 1, scale: 1.5 }}
                            className="absolute inset-0 bg-emerald-200/30 rounded-full blur-xl"
                        />
                    )}
                </div>

                {/* Text Content */}
                <div className="text-center space-y-2 mb-8 relative z-10">
                    <motion.h3
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-2xl font-bold text-slate-900"
                    >
                        Tiny Win Secured
                    </motion.h3>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6 }}
                        className="text-slate-500 font-medium uppercase tracking-widest text-xs"
                    >
                        {skillArea} Proficiency Level Up
                    </motion.p>
                </div>

                {/* The Bar */}
                <div className="relative h-4 bg-slate-100 rounded-full overflow-hidden mb-2">
                    <motion.div
                        className="absolute top-0 left-0 h-full bg-gradient-to-r from-emerald-500 to-cyan-500"
                        initial={{ width: "20%" }}
                        animate={{ width: "85%" }} // Simulate progress jump
                        transition={{ duration: 1.5, ease: "easeOut", delay: 0.8 }}
                    />
                </div>

                {/* Floating Numbers */}
                <div className="flex justify-between text-xs font-bold text-slate-400 mt-2">
                    <span>Level 1</span>
                    <motion.span
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 2 }}
                        className="text-emerald-600"
                    >
                        +50 XP
                    </motion.span>
                    <span>Level 2</span>
                </div>

                {/* Motivational Toast */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2.2 }}
                    className="mt-8 p-3 bg-slate-50 rounded-xl flex items-center gap-3 text-sm text-slate-600 border border-slate-100"
                >
                    <TrendingUp className="w-4 h-4 text-emerald-500" />
                    <span className="font-medium">You're building real momentum.</span>
                </motion.div>
            </motion.div>
        </div>
    );
};
